import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import PropTypes from 'prop-types';
import { Col, Badge, Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import {
  cellOperation,
  cellNumOperation,
  headerOperation,
  headerDoubleRowOperation,
} from 'helpers/tables';
import { uploadFile } from 'helpers/requests';
import { BASE_URL, API } from 'CONST';
import SafeBtn from 'components/shared/SafeBtn';
import BaseTable from '../shared/BaseTable';

const LibroVentaCargaMasivaModal = ({ openModal, toggleModal, libros, fileCargaMasiva }) => {
  const history = useHistory();
  const [loadingBtn, setLoadingBtn] = useState(false);
  const [librosStatus, setLibrosStatus] = useState({
    new: { texto: 'Libros de ventas nuevos', color: 'rgba(0, 253, 0, 0.5)', amount: 0 },
    update: {
      texto: 'Libros de ventas a sobreescribir',
      color: 'rgba(253, 253, 0, 0.5)',
      amount: 0,
    },
    error: {
      texto: 'Libros de ventas inválidos, no serán cargados',
      color: 'rgba(253, 0, 0, 0.5)',
      amount: 0,
    },
  });

  useEffect(() => {
    libros.forEach((libro) => {
      setLibrosStatus((prevStatus) => {
        if (libro.errors.length > 0) {
          return {
            ...prevStatus,
            error: { ...prevStatus.error, amount: prevStatus.error.amount + 1 },
          };
        }
        if (libro.sobreescritura) {
          return {
            ...prevStatus,
            update: { ...prevStatus.update, amount: prevStatus.update.amount + 1 },
          };
        }
        return {
          ...prevStatus,
          new: { ...prevStatus.new, amount: prevStatus.new.amount + 1 },
        };
      });
    });
  }, [libros]);

  const onCloseModal = () => {
    setLibrosStatus({
      ...librosStatus,
      error: { ...librosStatus.error, amount: 0 },
      new: { ...librosStatus.new, amount: 0 },
      update: { ...librosStatus.update, amount: 0 },
    });
    toggleModal();
  };

  const aceptarCarga = () => {
    setLoadingBtn(true);
    uploadFile(
      `${BASE_URL}${API.librosVentaBulkUpload}/?carga`,
      'carga-masiva',
      fileCargaMasiva,
    ).then(() => {
      history.go(0);
    });
  };

  const getTrProps = (state, rowInfo) => {
    let color = 'rgba(0, 253, 0, 0.5)'; // Nuevo = Verde
    if (rowInfo) {
      if (rowInfo.original.errors.length > 0) {
        color = 'rgba(253, 0, 0, 0.5)'; // Error = Rojo
      } else if (rowInfo.original.sobreescritura) {
        color = 'rgba(253, 253, 0, 0.5)'; // Actualizado = Amarillo
      }

      return {
        style: {
          background: color,
        },
      };
    }
    return {};
  };

  const columns = [
    {
      Header: headerOperation('Póliza'),
      id: 'poliza',
      accessor: (row) => row.poliza,
      Cell: (row) => cellOperation(row.value),
      minWidth: 100,
      sortable: false,
    },
    {
      Header: headerOperation('Vigencia'),
      id: 'vigencia',
      accessor: (row) => row.vigencia,
      Cell: (row) => cellNumOperation(row.value),
      minWidth: 60,
      sortable: false,
    },
    {
      Header: headerOperation('Periodo Venta'),
      id: 'periodo_venta',
      accessor: (row) => row.periodo_tag,
      Cell: (row) => cellOperation(row.value),
      minWidth: 100,
      sortable: false,
    },
    {
      Header: headerOperation('Documentos Declarados'),
      columns: [
        {
          Header: headerOperation('Cantidad'),
          id: 'cantidad_documentos_declarados',
          accessor: (row) => row.declarados_cantidad,
          Cell: (row) => cellNumOperation(row.value),
          minWidth: 60,
          sortable: false,
        },
        {
          Header: headerDoubleRowOperation('Monto Venta', 'Bruta (mm$)'),
          id: 'monto_documentos_declarados',
          accessor: (row) => row.declarados_monto,
          Cell: (row) => cellNumOperation(row.value),
          minWidth: 150,
          sortable: false,
        },
      ],
    },
    {
      Header: headerOperation('Documentos No Considerados'),
      columns: [
        {
          Header: headerOperation('Cantidad'),
          id: 'cantidad_documentos_no_considerados',
          accessor: (row) => row.no_considerados_cantidad,
          Cell: (row) => cellNumOperation(row.value),
          minWidth: 60,
          sortable: false,
        },
        {
          Header: headerDoubleRowOperation('Monto Venta', 'Bruta (mm$)'),
          id: 'monto_documentos_no_considerados',
          accessor: (row) => row.no_considerados_monto,
          Cell: (row) => cellNumOperation(row.value),
          minWidth: 150,
          sortable: false,
        },
      ],
    },
    {
      Header: headerOperation('Documentos Considerados'),
      columns: [
        {
          Header: headerOperation('Cantidad'),
          id: 'cantidad_documentos_considerados',
          accessor: (row) => row.considerados_cantidad,
          Cell: (row) => cellNumOperation(row.value),
          minWidth: 60,
          sortable: false,
        },
        {
          Header: headerDoubleRowOperation('Monto Venta', 'Bruta (mm$)'),
          id: 'monto_documentos_considerados',
          accessor: (row) => row.considerados_monto,
          Cell: (row) => cellNumOperation(row.value),
          minWidth: 150,
          sortable: false,
        },
      ],
    },
    {
      Header: headerDoubleRowOperation('Prima Neta Generada', '(MO)'),
      id: 'prima_neta_generada',
      accessor: (row) => row.prima_neta_generada,
      Cell: (row) => cellNumOperation(row.value, 'float'),
      minWidth: 120,
      sortable: false,
    },
  ];

  return (
    <>
      <Modal
        isOpen={openModal}
        style={{ maxWidth: '1240px' }}
        toggle={onCloseModal}
        type="modal-warning warning-container"
      >
        <ModalHeader style={{ paddingLeft: '6px', color: 'var(--orsan-blue)' }} toggle={onCloseModal}>
          Libros a Cargar
        </ModalHeader>
        <ModalBody>
          <Col md={{ size: 12 }}>
            <div className="orsan-form-element">
              La siguiente tabla es una previsualización de los libros de venta.
              Presione&nbsp;
              <b>Cargar</b>
              &nbsp;para hacer efectiva la carga.
            </div>
            <br />

            <ul style={{ listStyle: 'none', margin: 'auto', padding: '0' }}>
              {Object.values(librosStatus).map((libroStatus) => (
                <li
                  key={libroStatus.texto}
                  style={{
                    display: 'inline-block',
                    marginRight: '10px',
                  }}
                >
                  <h5>
                    <Badge style={{ backgroundColor: libroStatus.color }}>
                      {libroStatus.texto}
                      &nbsp;(
                      <b>{libroStatus.amount}</b>
                      )
                    </Badge>
                  </h5>
                </li>
              ))}
            </ul>

            <BaseTable
              columns={columns}
              data={libros}
              getTrProps={getTrProps}
              style={{ maxHeight: '63vh' }}
              resizable={false}
            />
          </Col>
        </ModalBody>
        <ModalFooter>
          <Button
            className="btn-orsan-secondary mr-auto"
            onClick={onCloseModal}
            disabled={loadingBtn}
            style={{ width: 165, marginLeft: 19 }}
          >
            Cancelar
          </Button>
          <SafeBtn
            className="btn-orsan-primary"
            onClick={aceptarCarga}
            disabled={libros.length === 0}
            loading={loadingBtn}
            style={{ width: 165, marginRight: 19 }}
          >
            Cargar
          </SafeBtn>
        </ModalFooter>
      </Modal>
    </>
  );
};

LibroVentaCargaMasivaModal.propTypes = {
  openModal: PropTypes.bool.isRequired,
  toggleModal: PropTypes.func.isRequired,
  libros: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
    }),
  ),
  fileCargaMasiva: PropTypes.shape({}),
};

LibroVentaCargaMasivaModal.defaultProps = {
  libros: [],
  fileCargaMasiva: null,
};

export default LibroVentaCargaMasivaModal;
